import { BodyM, Description, Heading2 } from '@casavo/base-ui'
import cn from 'classnames'
import { AnimatePresence } from 'framer-motion'
import useTranslation from 'next-translate/useTranslation'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useSessionStorage } from 'usehooks-ts'

import BannerCasavoProperties from '@/components/BannerCasavoProperties'
import { ContactAgencyForm } from '@/components/ContactAgencyForm'
import { FranceGeologicalRiskDisclaimer } from '@/components/FranceGeologicalRiskDisclaimer'
import GalleryButtons from '@/components/Gallery/Buttons'
import { DesktopImageGallery } from '@/components/Gallery/DesktopImageGallery'
import { MobileImageGallery } from '@/components/Gallery/MobileImageGallery'
import { InventoryBannerBig } from '@/components/InventoryBannerBig'
import { Layout } from '@/components/Layout'
import { contentLayoutInner, layoutContentInnerStretched } from '@/components/Layout/index.css'
import { ListingDetailSection } from '@/components/ListingDetailSection'
import CasavoProperties from '@/components/Modal/Types/CasavoProperties'
import { ModalDetailForm } from '@/components/Modal/Types/DetailForm'
import { MarkerClicked, ModalMap } from '@/components/Modal/Types/Map'
import {
  REQUEST_VISIT_INFO_AFTER_LOGIN,
  RequestContactStoredValues,
} from '@/components/RequestContactCTA/RequestContactFlow'
import { RequestContactModal, requestContactModalOptions } from '@/components/RequestContactCTA/RequestContactModal'
import { SimilarPropertiesCarousel } from '@/components/SimilarPropertiesCarousel'
import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { useRelatedAdsBanner } from '@/context/RelatedAdsBannerProvider'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { theme } from '@/design-lib/theme'
import ListingDetailHead from '@/heads/listing-detail'
import { usePageYOffset } from '@/hooks/usePageYOffset'
import { useTagManager } from '@/hooks/useTagManager'
import { baseRadius, headerHeightDesktop } from '@/shared-styles/common-variables'
import { sticky } from '@/shared-styles/styles.css'
import { Listings_Detail } from '@/types/cate-graphql-generated-types'
import { CasavoInventory } from '@/types/listing'
import { buildPoiFrom } from '@/types/poi'
import { CardDataListing, CardDataPriority } from '@/types/property-card'
import descriptionTranslations from '@/utils/description-translations'
import { getPreviousHourViews } from '@/utils/fomo-helper'
import getTranslatedTitle from '@/utils/get-translated-title'
import { getTranslatedStateOfMaintenance } from '@/utils/getTranslatedStateOfMaintenance'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { isLocaleEnabledFor } from '@/utils/locale-variables'
import { NearbyProperty } from '@/utils/nearby-properties-helper'
import { storePropertyVisited } from '@/utils/new-property'
import { nearbyUniqueByTypePois, sortedPointsOfInterestByDistance } from '@/utils/points-of-interest'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import {
  handleClickCallAgency,
  handleClickOpenCasavoPropertyModal,
  handleClickOpenMap,
  handleListingDetailClick,
  handleListingDetailMapClick,
} from '@/utils/tracking/eventsHandlers'

import AccordionDetailInfo from './AccordionDetailInfo'
import { casavoAdvantagesFor } from './CasavoAdvantage'
import Characteristics, { Characteristic } from './Characteristics'
import DetailContacts from './DetailContacts'
import DetailHeader from './DetailHeader'
import FomoMessage from './FomoMessage'
import { ListingDescription } from './ListingDescription'
import MobileContactUs from './MobileContactUs'
import { MortgageWidget } from './MortgageWidget'
import { NearbyPropertiesSection } from './NearbyPropertiesSection'
import SectionPoi from './PoiSection'
import { RelatedAdsBanner } from './RelatedAdsBanner'
import { relatedAdsBannerOnScrollStyle } from './RelatedAdsBanner/style.css'
import ServicesList from './ServicesList'
import {
  contentWrapper,
  detailGalleryBg,
  fomoMessageWrapper,
  responsiveContainerDetail,
  similarPropertiesWrapper,
} from './styles.css'

export const partialIdfromPropertySlug = (slug: string): string => {
  const slugSplittedByDash = slug.split('-')
  const lastPartOfTheSlug = slugSplittedByDash.length
  const positionOfThePartialId = lastPartOfTheSlug - 1
  return slugSplittedByDash[positionOfThePartialId]
}

export const parseListingPrice = (price: number | undefined): string | undefined => {
  if (Number.isFinite(price)) return Number(price / 100).toFixed(0)
}

type Props = {
  cities: ContextValue
  isDirectPageVisit?: boolean
  listing: Listings_Detail
  nearbyProperties: NearbyProperty[]
  similarListings: CardDataListing[]
  similarPriorities: CardDataPriority[]
}

const ListingDetail: React.FC<Props> = ({
  cities,
  isDirectPageVisit = false,
  listing,
  nearbyProperties,
  similarListings,
  similarPriorities,
}): ReactElement => {
  const device = useDevice()
  const { lang, t } = useTranslation()

  const [shouldShowPhoneNumber, setShouldShowPhoneNumber] = useState(false)
  const userViews = getPreviousHourViews(listing.id)

  const isIBuyerInventory = listing.detail.inventory === CasavoInventory.iBuyer
  const isOdaInventory = listing.detail.inventory === CasavoInventory.Agency
  const advantages = casavoAdvantagesFor(listing)

  const [showSimilarPropertiesBannerOnScroll, setShowSimilarPropertiesBannerOnScroll] = useState(false)
  const [showSimilarPropertiesBannerOnSwipe, setShowSimilarPropertiesBannerOnSwipe] = useState(false)
  const similarProperties = [...similarPriorities, ...similarListings]

  const { hasUserClickedOnBanner, showRelatedAdsBanner, similarPropertiesRef } = useRelatedAdsBanner()
  const shouldShowMortgageBanner =
    // widget only in Italian language
    lang === 'it' &&
    !listing.detail.privateNegotiation &&
    // FIXME this is always available, comes from nika_to_cate mapping
    listing.detail.sellingPrices &&
    // FIXME this is always a number, comes from nika_to_cate mapping, when price is null, the mapping performs a price*100 -> 0
    typeof listing.detail.sellingPrices.cents === 'number'

  const obfuscateExactLocation = isLocaleEnabledFor('obfuscateExactLocation', lang)
  const showPlanimetryButton = listing.country !== 'france'

  const [requestVisitInfoAfterLogin, setRequestVisitInfoAfterLogin] =
    useSessionStorage<null | RequestContactStoredValues>(REQUEST_VISIT_INFO_AFTER_LOGIN, null)

  usePageYOffset((scrollY) => {
    if (scrollY > 650) {
      setShowSimilarPropertiesBannerOnScroll(true)
    }
  })

  useTagManager(
    {
      agencyName: listing.contactInfo.agencyName,
      city: listing.detail.city.toLowerCase(),
      language: lang,
      listingId: listing.id,
      listingPrice: parseListingPrice(listing.detail?.sellingPrices?.cents),
      pageType: 'listing',
      propertyIsOwnedByCasavo: listing.contactInfo.ownedByCasavo,
      propertyStreetAddress:
        listing.addressesCate.street +
        ' ' +
        listing.addressesCate.streetNumber +
        ', ' +
        listing.addressesCate.zipCode +
        ', ' +
        listing.detail.city,
    },
    [listing.slug]
  )

  useEffect(() => {
    setShouldShowPhoneNumber(false)
  }, [listing.slug])

  const { closeModal, openModal } = useModal()

  const description = descriptionTranslations.get(listing.descriptions, lang)

  const pois = buildPoiFrom(listing.pointsOfInterest)

  const pointsOfInterest = sortedPointsOfInterestByDistance(pois)
  const closestPointsOfInterest = nearbyUniqueByTypePois(pois)

  useEffect(() => {
    if (isStorageSupported(() => sessionStorage) && typeof window.sessionStorage !== 'undefined') {
      sessionStorage.setItem('CASAVO@LISTING-BACK', 'true')
    }

    return () => {
      storePropertyVisited(listing.id)
    }
  }, [])

  useEffect(() => {
    if (requestVisitInfoAfterLogin !== null) {
      openModal(
        <RequestContactModal
          listing={listing}
          listingTitle={getTranslatedTitle(listing.detail?.titles, lang)}
          requestType={requestVisitInfoAfterLogin.requestType}
          similarPropertiesRef={similarPropertiesRef}
        />,
        requestContactModalOptions
      )
    }

    return () => setRequestVisitInfoAfterLogin(null)
  }, [])

  const handleOpenMapModalWithoutEvent = ({
    eventCategory,
    isReopening,
    selectedMarker,
  }: {
    eventCategory: string
    isReopening?: boolean
    selectedMarker?: MarkerClicked
  }) => {
    openModal(
      <ModalMap
        cities={cities}
        closeModal={closeModal}
        closestPointsOfInterest={closestPointsOfInterest}
        handleOpenDetailFormModal={handleOpenDetailFormModal(true)}
        handleTrackingCallAgency={handleTrackingCallAgency}
        listing={listing}
        pointsOfInterest={pointsOfInterest}
        selectedMarker={selectedMarker}
        similarPropertiesRef={similarPropertiesRef}
      />
    )

    if (!isReopening) {
      handleClickOpenMap(eventCategory)
      selectedMarker &&
        handleListingDetailMapClick(selectedMarker === 'property' ? 'PropertyMarker' : `Poi${selectedMarker.poiType}`)
    }
  }

  const onMapModalOpen = ({
    event,
    eventCategory,
    isReopening,
  }: {
    event: React.SyntheticEvent<HTMLElement>
    eventCategory: string
    isReopening?: boolean
  }) => {
    event && event.preventDefault()
    handleOpenMapModalWithoutEvent({ eventCategory, isReopening })
  }

  const clickShowNumberButtonHandler = () => {
    handleListingDetailClick('DisplayPhoneNumber')
    setShouldShowPhoneNumber(true)
  }

  const handleOpenDetailFormModal = (reopenMap: boolean) => (event: React.SyntheticEvent<HTMLElement>) => {
    event.preventDefault()
    openModal(
      <ModalDetailForm
        cities={cities}
        closeModal={() => {
          closeModal()
          // if (reopenMap) {
          //   onMapModalOpen({
          //     event,
          //     eventCategory: '',
          //     isReopening: reopenMap,
          //   })
          // }

          handleListingDetailClick('CloseContactModal')
        }}
        property={listing}
        similarPropertiesLength={similarProperties.length}
        similarPropertiesRef={similarPropertiesRef}
      />,
      { background: theme.colors.offWhite }
    )
    reopenMap ? handleListingDetailMapClick('RequestInfo') : handleListingDetailClick('RequestInfo')
  }

  const handleOpenCasavoPropertiesModal = () => {
    openModal(<CasavoProperties closeModal={() => closeModal()} />, {
      background: theme.colors.offWhite,
      borderRadius: baseRadius,
      desktopHeight: 903,
      desktopWidth: 1000,
      overlay: true,
    })
  }

  const handleTrackingCallAgency = (eventCategory: string) => {
    handleClickCallAgency(eventCategory)
  }

  const casavoInventoryBannerBigRef = useRef<HTMLDivElement>(null)

  // NOTE: we lack a proper Proprioo inventory by now :/
  const listingDescriptionEntrusted = isIBuyerInventory || isOdaInventory || listing.country === 'france'
  const characteristics: Characteristic[] = [
    {
      amount: listing.characteristics.bathrooms,
      icon: 'bathroom',
      title: 'bathrooms',
      value: listing.characteristics.bathrooms,
    },
    {
      amount: listing.characteristics.bedrooms,
      icon: 'bedroom',
      title: 'bedrooms',
      value: listing.characteristics.bedrooms,
    },
    {
      amount: listing.characteristics.balconies.counter,
      icon: 'balcony',
      title: 'balconies',
      value: listing.characteristics.balconies.counter,
    },
    {
      amount: listing.characteristics.terraces.counter,
      icon: 'terrace',
      title: 'terraces',
      value: listing.characteristics.terraces.counter,
    },
    {
      amount: getTranslatedStateOfMaintenance(listing.characteristics.stateOfMaintenance.toLowerCase(), t),
      icon: null,
      title: 'status',
      value: listing.characteristics.stateOfMaintenance,
    },
  ]

  const rooms: Characteristic = {
    amount: listing.characteristics.rooms,
    icon: 'rooms',
    title: 'rooms',
    value: listing.characteristics.rooms,
  }

  /* NEARBY PROPERTIES LOGIC ________________________________________________________________________ */

  const [showNearbyPropertiesBanner, setShowNearbyPropertiesBanner] = useState<boolean>(false)
  const [showNearbyPropertiesNavigation, setShowNearbyPropertiesNavigation] = useState<boolean>(false)

  const [isNearbyPropertiesNavigationSticky, setIsNearbyPropertiesNavigationSticky] =
    useState<boolean>(showNearbyPropertiesNavigation)

  const navigationBannerHeight = isNearbyPropertiesNavigationSticky ? 53 : 0

  useEffect(() => {
    if (nearbyProperties.length > 1 && isDirectPageVisit) {
      setShowNearbyPropertiesBanner(!showNearbyPropertiesNavigation)
    }
  }, [isDirectPageVisit, nearbyProperties, showNearbyPropertiesNavigation])

  useEffect(() => {
    // Hide banner when user enters navigation
    if (showNearbyPropertiesNavigation) setShowNearbyPropertiesBanner(false)
  }, [showNearbyPropertiesNavigation])

  useEffect(() => {
    // Nearby Properties Banner is always sticky on mobile
    setIsNearbyPropertiesNavigationSticky(device === 'mobile')
  }, [device, setIsNearbyPropertiesNavigationSticky])

  useEffect(() => {
    // Nearby Properties Banner becomes sticky when navigating
    if (showNearbyPropertiesNavigation) setIsNearbyPropertiesNavigationSticky(true)
  }, [showNearbyPropertiesNavigation, setIsNearbyPropertiesNavigationSticky])

  return (
    <div className={sprinkles({ position: 'relative' })}>
      <ListingDetailHead cities={cities} listing={listing} />
      <Layout cities={cities} contentWidth="full" showHeaderOnMobile={false}>
        <NearbyPropertiesSection
          cities={cities}
          isSticky={isNearbyPropertiesNavigationSticky}
          listing={listing}
          nearbyProperties={nearbyProperties}
          setShowNavigation={setShowNearbyPropertiesNavigation}
          showBanner={showNearbyPropertiesBanner}
          showNavigation={showNearbyPropertiesNavigation}
        />
        <div className={contentWrapper}>
          <div className={cn(layoutContentInnerStretched, detailGalleryBg)}>
            <div className={contentLayoutInner.default}>
              {device === 'mobile' ? (
                <MobileImageGallery
                  cities={cities}
                  citySlug={listing.detail.city}
                  eventCategory={GTMEventCategory.ListingDetail}
                  photos={listing.photos}
                  planimetryUrl={listing.planimetries?.url}
                  swipeCallback={(index) => {
                    index >= Math.round(listing.photos.length / 2) && setShowSimilarPropertiesBannerOnSwipe(true)
                  }}
                  title={getTranslatedTitle(listing.detail?.titles, lang)}
                />
              ) : (
                <DesktopImageGallery
                  cities={cities}
                  citySlug={listing.detail.city}
                  eventCategory={GTMEventCategory.ListingDetail}
                  id={listing.id}
                  photos={listing.photos}
                  planimetryUrl={listing.planimetries?.url}
                  title={getTranslatedTitle(listing.detail?.titles, lang)}
                />
              )}
              <GalleryButtons
                cities={cities}
                obfuscateExactLocation={obfuscateExactLocation}
                openMap={(event) => onMapModalOpen({ event, eventCategory: 'ListingDetail' })}
                property={listing}
                showFloorPlan={showPlanimetryButton}
              />
            </div>
          </div>

          <div className={responsiveContainerDetail}>
            {device === 'mobile' && showRelatedAdsBanner && (
              <>
                <AnimatePresence>
                  {showSimilarPropertiesBannerOnSwipe && !showSimilarPropertiesBannerOnScroll && (
                    <RelatedAdsBanner touchPoint="FromCarousel" />
                  )}
                </AnimatePresence>
                <AnimatePresence>
                  {showSimilarPropertiesBannerOnScroll && !hasUserClickedOnBanner && (
                    <RelatedAdsBanner
                      className={cn(sprinkles({ borderRadius: 0 }), sticky, relatedAdsBannerOnScrollStyle)}
                      touchPoint="FromPage"
                    />
                  )}
                </AnimatePresence>
              </>
            )}

            <div>
              <DetailHeader cities={cities} listing={listing} />

              <Characteristics characteristics={characteristics} rooms={rooms} />
              <ServicesList listing={listing} />
              <AccordionDetailInfo listing={listing} />
              <div className={sprinkles({ paddingBottom: '2xl' })}>
                <ListingDetailSection hasBorder wide title={t('listing-detail:description')}>
                  <ListingDescription
                    safeContent={listingDescriptionEntrusted}
                    text={description.propertyDescription}
                  />
                </ListingDetailSection>
                {description.neighbourhoodDescription && (
                  <ListingDetailSection wide title={t('listing-detail:the_district')}>
                    <BodyM>{description.neighbourhoodDescription}</BodyM>
                  </ListingDetailSection>
                )}
                <FranceGeologicalRiskDisclaimer country={listing.country as CateCountry} />
              </div>

              {!obfuscateExactLocation && (
                <SectionPoi
                  cities={cities}
                  closestPointsOfInterest={closestPointsOfInterest}
                  handleTrackingCallAgency={handleTrackingCallAgency}
                  listing={listing}
                  pointsOfInterest={pointsOfInterest}
                  similarPropertiesRef={similarPropertiesRef}
                  onMapModalOpen={onMapModalOpen}
                  onOpenDetailFormModal={handleOpenDetailFormModal}
                  onOpenMapModalWithoutEvent={handleOpenMapModalWithoutEvent}
                />
              )}

              {isIBuyerInventory && (
                <BannerCasavoProperties
                  onClickCta={() => {
                    handleOpenCasavoPropertiesModal()
                    handleClickOpenCasavoPropertyModal('ListingDetail')
                  }}
                />
              )}

              {shouldShowMortgageBanner && (
                <MortgageWidget listingId={listing.id} sellingPricesInCents={listing.detail.sellingPrices.cents} />
              )}
            </div>

            {device === 'desktop' && (
              <div>
                <div
                  style={{
                    position: 'sticky',
                    top: `calc(${headerHeightDesktop}px + ${navigationBannerHeight}px + ${theme.spacings.l})`,
                  }}
                >
                  {showRelatedAdsBanner && device === 'desktop' && (
                    <AnimatePresence>
                      <RelatedAdsBanner touchPoint="FromPage" />
                    </AnimatePresence>
                  )}
                  <DetailContacts
                    advantages={advantages}
                    clickShowNumberButtonHandler={clickShowNumberButtonHandler}
                    property={listing}
                    shouldShowPhoneNumber={shouldShowPhoneNumber}
                    similarPropertiesRef={similarPropertiesRef}
                  />
                  {isLocaleEnabledFor('fomo', lang) && (
                    <p className={fomoMessageWrapper}>
                      <FomoMessage userViews={userViews} />
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {isOdaInventory && (
            <div ref={casavoInventoryBannerBigRef}>
              <InventoryBannerBig advantages={advantages} inventory={listing.detail.inventory as CasavoInventory} />
            </div>
          )}

          {device === 'mobile' ? (
            <MobileContactUs
              cities={cities}
              handleOpenDetailFormModal={handleOpenDetailFormModal}
              handleTrackingCallAgency={handleTrackingCallAgency}
              property={listing}
              similarPropertiesLength={similarProperties.length}
              similarPropertiesRef={similarPropertiesRef}
            />
          ) : (
            <div id="contactForm">
              <ContactAgencyForm
                cities={cities}
                property={listing}
                similarPropertiesLength={similarProperties.length}
                similarPropertiesRef={similarPropertiesRef}
              />
            </div>
          )}
          {similarProperties.length > 0 && (
            <div ref={similarPropertiesRef} className={similarPropertiesWrapper} id="similarProperties">
              <div
                className={sprinkles({ paddingBottom: '4xl', paddingTop: '2xl', paddingX: '4xl', textAlign: 'center' })}
              >
                <Heading2>{t('listing-detail:might_interest_you')}</Heading2>
                <Description className={sprinkles({ marginTop: 's' })}>
                  {t('listing-detail:based_on_your_searches')}
                </Description>
              </div>
              <SimilarPropertiesCarousel similarListings={similarListings} similarPriorities={similarPriorities} />
            </div>
          )}
        </div>
      </Layout>
    </div>
  )
}

export default ListingDetail
